import { Building2, Check, ChevronsUpDown, Loader2, Plus, ShieldCheck, UserCircle, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth/authStore";
import { cn } from "@/lib/utils/cn";
import {
  useCreateFunctionalAccountMutation,
  useSwitchFunctionalAccountMutation,
} from "@/features/auth/hooks/useAuthMutations";
import type { FunctionalAccount, FunctionalAccountStatus, FunctionalAccountType } from "@/features/auth/types";
import { getFunctionalAccountDisplayName, getFunctionalAccountLabel } from "@/features/auth/utils/functionalAccountLabels";
import { Button, Input, Label } from "@/shared/ui";

const functionalAccountOptions: Array<{ value: FunctionalAccountType; label: string }> = [
  { value: "basic", label: "Basic" },
  { value: "patient", label: "Patient" },
  { value: "doctor", label: "Doctor" },
  { value: "specialist", label: "Specialist" },
  { value: "technician", label: "Technician" },
  { value: "business_owner", label: "Business Owner" },
];

export interface AccountManagementPanelProps {
  compact?: boolean;
  createOnly?: boolean;
  createFormRequestKey?: number;
  requestedFunctionalAccountType?: FunctionalAccountType;
  onClose?: () => void;
  onManageAccounts?: () => void;
}

export const AccountManagementPanel = ({
  compact = false,
  createOnly = false,
  createFormRequestKey,
  requestedFunctionalAccountType,
  onClose,
  onManageAccounts,
}: AccountManagementPanelProps): JSX.Element => {
  const navigate = useNavigate();
  const availableFunctionalAccounts = useAuthStore((state) => state.availableFunctionalAccounts);
  const activeFunctionalAccount = useAuthStore((state) => state.activeFunctionalAccount);
  const createFunctionalAccountMutation = useCreateFunctionalAccountMutation();
  const switchFunctionalAccountMutation = useSwitchFunctionalAccountMutation();
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [functionalAccountType, setFunctionalAccountType] = useState<FunctionalAccountType>("patient");
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    if (createFormRequestKey !== undefined) {
      if (requestedFunctionalAccountType) {
        setFunctionalAccountType(requestedFunctionalAccountType);
      }
      setIsCreateFormOpen(true);
    }
  }, [createFormRequestKey, requestedFunctionalAccountType]);

  const sortedAccounts = useMemo(
    () =>
      [...availableFunctionalAccounts].sort((leftAccount, rightAccount) => {
        if (leftAccount.id === activeFunctionalAccount?.id) return -1;
        if (rightAccount.id === activeFunctionalAccount?.id) return 1;
        return leftAccount.functional_account_type.localeCompare(rightAccount.functional_account_type);
      }),
    [activeFunctionalAccount?.id, availableFunctionalAccounts],
  );

  const createError = createFunctionalAccountMutation.error ? getApiErrorMessage(createFunctionalAccountMutation.error) : null;
  const switchError = switchFunctionalAccountMutation.error ? getApiErrorMessage(switchFunctionalAccountMutation.error) : null;

  const submitCreateFunctionalAccount = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (functionalAccountType === "business_owner") {
      onClose?.();
      navigate("/app/business/register");
      return;
    }

    createFunctionalAccountMutation.mutate(
      {
        functional_account_type: functionalAccountType,
        display_name: displayName.trim() || null,
      },
      {
        onSuccess: () => {
          setDisplayName("");
          setIsCreateFormOpen(false);
          if (createOnly) {
            onClose?.();
          }
        },
      },
    );
  };

  const switchFunctionalAccount = (functionalAccount: FunctionalAccount): void => {
    if (functionalAccount.id === activeFunctionalAccount?.id || switchFunctionalAccountMutation.isPending) {
      return;
    }

    switchFunctionalAccountMutation.mutate(
      { functional_account_id: Number(functionalAccount.id) },
      {
        onSuccess: () => {
          onClose?.();
          navigate(resolveFunctionalAccountLandingPath(functionalAccount.functional_account_type), { replace: true });
        },
      },
    );
  };

  return (
    <section className={cn("min-w-0", compact ? "space-y-3" : "space-y-5")}>
      {!createOnly ? (
        <>
          <div className="flex min-w-0 items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-card-foreground">My Accounts</p>
            </div>
            {onManageAccounts ? (
              <Button type="button" size="sm" variant="outline" className="shrink-0 rounded-2xl" onClick={onManageAccounts}>
                Manage
              </Button>
            ) : null}
          </div>

          <div className="grid gap-2">
            {sortedAccounts.length > 0 ? (
              sortedAccounts.map((functionalAccount) => {
                const isActive = functionalAccount.id === activeFunctionalAccount?.id;

                return (
                  <button
                    key={functionalAccount.id}
                    type="button"
                    className={cn(
                      "flex w-full min-w-0 items-center gap-3 rounded-2xl border border-border bg-card/70 p-3 text-left transition hover:border-primary/35 hover:bg-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary/30",
                      isActive && "border-primary/45 bg-primary/5",
                    )}
                    onClick={() => switchFunctionalAccount(functionalAccount)}
                    disabled={isActive || switchFunctionalAccountMutation.isPending}
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                      {isActive ? <Check className="h-5 w-5" aria-hidden="true" /> : <UserCircle className="h-5 w-5" aria-hidden="true" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-card-foreground">
                        {functionalAccount.display_name || getFunctionalAccountLabel(functionalAccount.functional_account_type)}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        <span>{getFunctionalAccountLabel(functionalAccount.functional_account_type)}</span>
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-secondary-foreground">
                          {getStatusLabel(functionalAccount.status)}
                        </span>
                      </span>
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  </button>
                );
              })
            ) : (
              <div className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                Nuk ka functional accounts te sinkronizuara ende.
              </div>
            )}
          </div>

          {switchError ? <p className="text-xs font-medium text-destructive">{switchError}</p> : null}
        </>
      ) : null}

      <div className="rounded-2xl border border-border bg-[hsl(var(--surface))]/65 p-3">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 text-left"
          onClick={() => setIsCreateFormOpen((current) => !current)}
          aria-expanded={isCreateFormOpen}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
              <Plus className="h-4 w-4" aria-hidden="true" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-card-foreground">Create Account</span>
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>

        {isCreateFormOpen ? (
          <form className="mt-3 grid gap-3" onSubmit={submitCreateFunctionalAccount}>
            <div className="grid gap-1.5">
              <Label htmlFor={compact ? "functional-account-type-compact" : "functional-account-type"}>Account type</Label>
              <select
                id={compact ? "functional-account-type-compact" : "functional-account-type"}
                className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground app-focus"
                value={functionalAccountType}
                onChange={(event) => setFunctionalAccountType(event.target.value as FunctionalAccountType)}
              >
                {functionalAccountOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor={compact ? "functional-account-display-name-compact" : "functional-account-display-name"}>
                Display name
              </Label>
              <Input
                id={compact ? "functional-account-display-name-compact" : "functional-account-display-name"}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="P.sh. Profili profesional"
                maxLength={255}
              />
            </div>

            {createError ? <p className="text-xs font-medium text-destructive">{createError}</p> : null}

            <Button type="submit" className="rounded-2xl" disabled={createFunctionalAccountMutation.isPending}>
              {createFunctionalAccountMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Create Account
            </Button>
          </form>
        ) : null}
      </div>

    </section>
  );
};

export interface AccountManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  createFormRequestKey: number;
  requestedFunctionalAccountType: FunctionalAccountType;
}

export const AccountManagementDialog = ({
  open,
  onOpenChange,
  createFormRequestKey,
  requestedFunctionalAccountType,
}: AccountManagementDialogProps): JSX.Element => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-[80] bg-slate-950/45 backdrop-blur-sm" />
      <Dialog.Content className="fixed left-1/2 top-1/2 z-[81] max-h-[min(86vh,720px)] w-[min(94vw,520px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[26px] border border-border bg-card p-5 text-card-foreground shadow-2xl sm:p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Dialog.Title className="text-lg font-bold text-foreground">Shto account</Dialog.Title>
            <Dialog.Description className="sr-only">
              Krijo nje account funksional.
            </Dialog.Description>
          </div>
          <Dialog.Close asChild>
            <Button type="button" variant="ghost" size="icon" className="shrink-0 rounded-full" aria-label="Mbyll">
              <X className="h-5 w-5" aria-hidden="true" />
            </Button>
          </Dialog.Close>
        </div>
        <AccountManagementPanel
          compact
          createOnly
          createFormRequestKey={createFormRequestKey}
          requestedFunctionalAccountType={requestedFunctionalAccountType}
          onClose={() => onOpenChange(false)}
        />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

export const AccountSwitcherInline = (): JSX.Element => {
  const navigate = useNavigate();
  const activeFunctionalAccount = useAuthStore((state) => state.activeFunctionalAccount);
  const availableFunctionalAccounts = useAuthStore((state) => state.availableFunctionalAccounts);
  const switchFunctionalAccountMutation = useSwitchFunctionalAccountMutation();
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const activeLabel = activeFunctionalAccount
    ? getFunctionalAccountDisplayName(activeFunctionalAccount)
    : "Shto account";

  const switchFunctionalAccount = (functionalAccount: FunctionalAccount): void => {
    if (functionalAccount.id === activeFunctionalAccount?.id || switchFunctionalAccountMutation.isPending) {
      return;
    }

    switchFunctionalAccountMutation.mutate(
      { functional_account_id: Number(functionalAccount.id) },
      {
        onSuccess: () => {
          setIsSwitcherOpen(false);
          navigate(resolveFunctionalAccountLandingPath(functionalAccount.functional_account_type), { replace: true });
        },
      },
    );
  };

  return (
    <div className="relative min-w-0">
      <button
        type="button"
        className="inline-flex h-10 max-w-[9.5rem] min-w-0 items-center gap-2 rounded-full border border-border bg-card px-3 text-sm font-semibold text-card-foreground shadow-sm transition hover:border-primary/35 hover:bg-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 sm:max-w-64"
        aria-haspopup="menu"
        aria-expanded={isSwitcherOpen}
        onClick={() => setIsSwitcherOpen((current) => !current)}
      >
        <ShieldCheck className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <span className="truncate">{activeLabel}</span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>

      {isSwitcherOpen ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[64] cursor-default"
            aria-label="Mbyll account switcher"
            onClick={() => setIsSwitcherOpen(false)}
          />
          <div
            className="absolute left-0 top-12 z-[72] w-[min(20rem,calc(100vw-1.5rem))] overflow-hidden rounded-[22px] border border-border bg-card text-card-foreground shadow-2xl"
            role="menu"
          >
            <div className="grid gap-1 p-2">
              {availableFunctionalAccounts.length > 0 ? (
                availableFunctionalAccounts.map((functionalAccount) => {
                  const isActive = functionalAccount.id === activeFunctionalAccount?.id;
                  const Icon = getFunctionalAccountIcon(functionalAccount.functional_account_type);

                  return (
                    <button
                      key={functionalAccount.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={isActive}
                      className={cn(
                        "flex w-full min-w-0 items-center gap-3 rounded-2xl px-3 py-3 text-left transition hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary/30",
                        isActive && "bg-primary/5",
                      )}
                      disabled={isActive || switchFunctionalAccountMutation.isPending}
                      onClick={() => switchFunctionalAccount(functionalAccount)}
                    >
                      <Icon className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">
                          {getFunctionalAccountDisplayName(functionalAccount)}
                        </span>
                      </span>
                      {isActive ? (
                        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                          Aktiv
                        </span>
                      ) : null}
                    </button>
                  );
                })
              ) : (
                <div className="rounded-2xl border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
                  Nuk ka account aktiv.
                </div>
              )}
            </div>

            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-3 border-t border-border px-5 py-4 text-left text-sm font-semibold text-primary transition hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/30"
              onClick={() => {
                setIsSwitcherOpen(false);
                navigate("/app/dashboard");
              }}
            >
              <Plus className="h-5 w-5" aria-hidden="true" />
              Shto account te ri
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-3 border-t border-border px-5 py-4 text-left text-sm font-semibold text-primary transition hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/30"
              onClick={() => {
                setIsSwitcherOpen(false);
                navigate("/app/business/register");
              }}
            >
              <Building2 className="h-5 w-5" aria-hidden="true" />
              Shto Business Account
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};

const getFunctionalAccountIcon = (functionalAccountType: FunctionalAccountType): LucideIcon => {
  if (functionalAccountType === "business_owner") {
    return Building2;
  }

  if (functionalAccountType === "basic") {
    return ShieldCheck;
  }

  return UserCircle;
};

const resolveFunctionalAccountLandingPath = (functionalAccountType: FunctionalAccountType): string => {
  const landingPaths: Record<FunctionalAccountType, string> = {
    basic: "/app/dashboard",
    patient: "/app/patient",
    doctor: "/app/professional/settings",
    specialist: "/app/professional/settings",
    technician: "/app/laboratory",
    business_owner: "/app/business/manage",
  };

  return landingPaths[functionalAccountType] ?? "/app/dashboard";
};

const getStatusLabel = (status: FunctionalAccountStatus): string => {
  const labels: Record<FunctionalAccountStatus, string> = {
    draft: "Draft",
    pending_verification: "Pending Verification",
    verified: "Verified",
    rejected: "Rejected",
    disabled: "Disabled",
  };

  return labels[status];
};
